import type { Ref } from 'vue';
import type { BlockSerialized, BlockSerializedChild } from '#layers/block-engine/types/block';
import { computed, ref } from 'vue';

export function useBlockEditorSelection({
	blockData,
}: {
	blockData: Ref<BlockSerialized | null>;
}) {
	const selectedChildIds = ref<Array<string>>([]);

	const selectedChildren = computed(() => selectedChildIds.value.map((childId) => {
		return blockData.value?.children?.[childId] ?? null;
	}).filter(Boolean) as Array<BlockSerializedChild>);
	const firstSelectedChild = computed(() => selectedChildren.value[0]);

	function add(childId: string) {
		if (!selectedChildIds.value.includes(childId)) {
			selectedChildIds.value.push(childId);
		}
	}

	function remove(childId: string) {
		const index = selectedChildIds.value.indexOf(childId);

		if (index !== -1) {
			selectedChildIds.value.splice(index, 1);
		}
	}

	function toggle(childId: string) {
		if (selectedChildIds.value.includes(childId)) {
			remove(childId);
		}
		else {
			add(childId);
		}
	}

	function clear() {
		selectedChildIds.value = [];
	}

	// Hover

	const hoveredChildId = ref<string | null>(null);
	const hoveredChild = computed(() => {
		if (hoveredChildId.value) {
			return blockData.value?.children?.[hoveredChildId.value] ?? null;
		}

		return null;
	});

	/**
	 * Hovered when the context menu was opened
	 */
	const contextMenuChildId = ref<string | null>(null);

	return {
		selectedChildIds,
		selectedChildren,
		firstSelectedChild,
		add,
		remove,
		toggle,
		clear,
		hoveredChildId,
		hoveredChild,
		contextMenuChildId,
	};
}

export type BlockEditorSelection = ReturnType<typeof useBlockEditorSelection>;
